/*
 * 웅동 임시 화물차 주차장 정기권 신청 페이지 (ParkingMainPage)
 * 
 * ⚠️  임시 접근 제한 적용됨 ⚠️
 * 
 * 현재 이 페이지는 순차 오픈을 위해 특정 권한의 사용자만 접근할 수 있도록 제한되어 있습니다.
 * 
 * 제한 조건:
 * - userLevel 4 이하: 접근 허용 (공사 관계자, 센터 관계자, 시스템 관리자)
 * - userLevel 5 이상: 접근 제한 → /site-preparation 페이지로 리다이렉트
 * 
 * 정식 오픈 시 해제 방법:
 * 1. TEMPORARY_ACCESS_CONTROL 상수를 false로 변경
 * 2. 관련 주석 및 코드 블록 제거
 * 3. 사용자 인증 로직을 원래대로 복원
 * 
 * 검색 키워드: "임시 접근 제한", "순차 오픈용", "TEMPORARY_ACCESS_CONTROL"
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import ClientLayout from '@/components/layout/ClientLayout';

// ===== 임시 접근 제한 설정 (순차 오픈용) =====
// TODO: 사이트 정식 오픈 시 이 플래그를 false로 변경하여 접근 제한을 해제하세요
const TEMPORARY_ACCESS_CONTROL = true;
// 허용되는 최대 userLevel (4 이하만 접근 가능, 5 이상은 제한)
const MAX_ALLOWED_USER_LEVEL = 4;
// ============================================

interface ParkingApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  applicationType: 'chassis' | 'individual'; // 섀시업체 | 개별화물차
  period: 1 | 2 | 3; // 1~3개월
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: any;
  updatedAt: any;
  termsAgreed: boolean;
  additionalRequest?: string;
  periodId?: string; // 신청 차수 ID
}

interface ParkingPeriod {
  id: string;
  sequence: number;
  applicationStartDate: any;
  applicationEndDate: any;
  lotteryDate: any;
  usageStartDate: any;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

const ParkingMainPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [application, setApplication] = useState<ParkingApplication | null>(null);
  const [currentPeriod, setCurrentPeriod] = useState<ParkingPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const user = auth.currentUser;

  useEffect(() => {
    const checkUserAccessAndFetchData = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }

      // ===== 임시 접근 제한 체크 (순차 오픈용) =====
      if (TEMPORARY_ACCESS_CONTROL && user) {
        try {
          const userProfileRef = doc(db, 'user_profiles', user.uid);
          const userProfileSnap = await getDoc(userProfileRef);
          
          if (userProfileSnap.exists()) {
            const userProfileData = userProfileSnap.data();
            const userLevel = userProfileData.userLevel || 999; // 기본값: 999 (최고 제한)
            
            // userLevel이 MAX_ALLOWED_USER_LEVEL보다 큰 경우 접근 제한
            if (userLevel > MAX_ALLOWED_USER_LEVEL) {
              console.log(`접근 제한: userLevel ${userLevel} > ${MAX_ALLOWED_USER_LEVEL}`);
              navigate('/site-preparation');
              return;
            }
          } else {
            // 사용자 프로필이 없는 경우도 접근 제한
            console.log('접근 제한: 사용자 프로필 없음');
            navigate('/site-preparation');
            return;
          }
        } catch (error) {
          console.error('사용자 권한 확인 실패:', error);
          navigate('/site-preparation');
          return;
        }
      }
      // ============================================

      await fetchData();
    };

    const fetchData = async () => {
      try {
        // 현재 활성화된 신청 차수 조회
        const periodsQuery = query(
          collection(db, 'parking_periods'),
          where('isActive', '==', true),
          orderBy('sequence', 'desc')
        );
        const periodsSnapshot = await getDocs(periodsQuery);
        
        if (!periodsSnapshot.empty) {
          const periodDoc = periodsSnapshot.docs[0];
          setCurrentPeriod({ id: periodDoc.id, ...periodDoc.data() } as ParkingPeriod);
        }

        // 사용자 신청 내역 조회
        const applicationsQuery = query(
          collection(db, 'parking_applications'),
          where('uid', '==', user!.uid),
          orderBy('createdAt', 'desc')
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        if (!applicationsSnapshot.empty) {
          const doc = applicationsSnapshot.docs[0];
          setApplication({ id: doc.id, ...doc.data() } as ParkingApplication);
        }
      } catch (error) {
        console.error('데이터 조회 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUserAccessAndFetchData();
  }, [user, navigate]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '신청 대기중';
      case 'approved':
        return '신청 승인';
      case 'rejected':
        return '신청 거절';
      case 'cancelled':
        return '신청 취소';
      default:
        return '알 수 없음';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-500';
      case 'approved':
        return 'text-green-500';
      case 'rejected':
        return 'text-red-500';
      case 'cancelled':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  const getApplicationTypeText = (type: string) => {
    return type === 'chassis' ? '섀시업체 신청' : '개별화물차 신청';
  };

  const isApplicationPeriodActive = () => {
    if (!currentPeriod) return false;
    
    const now = new Date();
    const startDate = currentPeriod.applicationStartDate?.toDate?.();
    const endDate = currentPeriod.applicationEndDate?.toDate?.();
    
    return startDate && endDate && now >= startDate && now <= endDate;
  };

  const canModifyApplication = () => {
    if (!application || !currentPeriod) return false;
    
    // 현재 신청이 현재 활성화된 차수에 속하는지 확인
    const isCurrentPeriodApplication = application.periodId === currentPeriod.id;
    
    // 신청 기간 내이고 현재 차수의 신청이며 pending 또는 rejected 상태인 경우만 수정 가능
    return isApplicationPeriodActive() && 
           isCurrentPeriodApplication && 
           (application.status === 'pending' || application.status === 'rejected');
  };

  const canCancelApplication = () => {
    if (!application || !currentPeriod) return false;
    
    // 현재 신청이 현재 활성화된 차수에 속하는지 확인
    const isCurrentPeriodApplication = application.periodId === currentPeriod.id;
    
    // 신청 기간 내이고 현재 차수의 신청이며 cancelled가 아닌 경우 취소 가능
    return isApplicationPeriodActive() && 
           isCurrentPeriodApplication && 
           application.status !== 'cancelled';
  };

  const getPeriodStatusText = () => {
    if (!currentPeriod) return null;
    
    const now = new Date();
    const startDate = currentPeriod.applicationStartDate?.toDate?.();
    const endDate = currentPeriod.applicationEndDate?.toDate?.();
    const lotteryDate = currentPeriod.lotteryDate?.toDate?.();
    
    if (now < startDate) {
      return `${currentPeriod.sequence}차 신청 예정 (${startDate?.toLocaleDateString('ko-KR')} 시작)`;
    } else if (now >= startDate && now <= endDate) {
      return `${currentPeriod.sequence}차 신청 진행중 (${endDate?.toLocaleDateString('ko-KR')}까지)`;
    } else if (now > endDate && now < lotteryDate) {
      return `${currentPeriod.sequence}차 신청 마감 (추첨일: ${lotteryDate?.toLocaleDateString('ko-KR')})`;
    } else {
      return `${currentPeriod.sequence}차 추첨 완료`;
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-lg text-gray-600">로딩 중...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-[760px] mx-auto relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
                웅동 임시 화물차 주차장 정기권 신청
              </div>
              {currentPeriod && !application && (
                <div className="text-right">
                  <div className="text-[#1e2c49] text-lg font-semibold">
                    {currentPeriod.sequence}차 신청
                  </div>
                  <div className="text-[#6b7684] text-sm">
                    {getPeriodStatusText()}
                  </div>
                </div>
              )}
              {application && (
                <div className={`px-4 py-1.5 rounded-[5px] inline-flex items-center gap-2.5 ${
                  application.status === 'pending' ? 'bg-[#f2f6fb]' :
                  application.status === 'approved' ? 'bg-green-50' :
                  application.status === 'rejected' ? 'bg-red-50' :
                  'bg-gray-50'
                }`}>
                  <div className="w-6 h-6 relative">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path 
                        d="M12 8V12L15 15" 
                        stroke={
                          application.status === 'pending' ? '#42506e' :
                          application.status === 'approved' ? '#16a34a' :
                          application.status === 'rejected' ? '#dc2626' :
                          '#6b7280'
                        } 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                      <circle 
                        cx="12" 
                        cy="12" 
                        r="9" 
                        stroke={
                          application.status === 'pending' ? '#42506e' :
                          application.status === 'approved' ? '#16a34a' :
                          application.status === 'rejected' ? '#dc2626' :
                          '#6b7280'
                        } 
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <span className={`text-lg font-medium ${
                    application.status === 'pending' ? 'text-[#42506e]' :
                    application.status === 'approved' ? 'text-green-700' :
                    application.status === 'rejected' ? 'text-red-700' :
                    'text-gray-700'
                  }`}>현재 상태</span>
                  <span className={`text-lg font-bold ${
                    application.status === 'pending' ? 'text-[#1b89e5]' :
                    application.status === 'approved' ? 'text-green-600' :
                    application.status === 'rejected' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>{getStatusText(application.status)}</span>
                </div>
              )}
            </div>
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
            <div className="mt-8 flex items-center justify-between">
              <div className="text-[#1e2c49] text-2xl font-bold font-['Pretendard'] leading-9">
                {application ? '신청 현황' : '새로운 신청'}
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {application ? (
            <>
              {/* 신청 정보 카드 */}
              <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
                <div className="p-8">
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    신청 정보
                  </h2>
                  <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">신청 유형</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">{getApplicationTypeText(application.applicationType)}</p>
                    </div>
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">신청 기간</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">{application.period}개월</p>
                    </div>
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">신청자명</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">{application.name}</p>
                    </div>
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">소속</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">{application.organization}</p>
                    </div>
                  </div>
                </div>

                {/* 신청일시 */}
                <div className="p-8 border-t border-[#e5e8eb]">
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">신청일시</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">
                        {application.createdAt?.toDate?.()?.toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) || '알 수 없음'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#6b7684] text-lg font-medium mb-2">최종 수정</p>
                      <p className="text-[#1e2c49] text-[19px] font-semibold">
                        {application.updatedAt?.toDate?.()?.toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) || '알 수 없음'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 추가 요청사항 */}
                {application.additionalRequest && (
                  <div className="p-8 border-t border-[#e5e8eb]">
                    <h3 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      추가 요청사항
                    </h3>
                    <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                    <p className="text-[#1e2c49] text-[19px] font-semibold whitespace-pre-wrap">
                      {application.additionalRequest}
                    </p>
                  </div>
                )}
              </div>

              {/* 안내 메시지 */}
              <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
                <div className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 relative mt-1">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" 
                          stroke="#1b89e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 8V12L16 14" stroke="#1b89e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-[#1e2c49] text-lg font-semibold mb-2">안내사항</p>
                      <p className="text-[#6b7684] text-base leading-relaxed">
                        {application.status === 'pending' && 
                          '신청이 접수되었습니다. 관리자 검토 후 추첨이 진행될 예정입니다.'
                        }
                        {application.status === 'approved' && 
                          '신청이 승인되었습니다. 추첨 결과를 기다려 주세요.'
                        }
                        {application.status === 'rejected' && 
                          '신청이 거절되었습니다. 새로운 신청을 위해서는 기존 신청을 취소해 주세요.'
                        }
                        {application.status === 'cancelled' && 
                          '신청이 취소되었습니다. 새로운 신청을 하실 수 있습니다.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 하단 여백 - 고정 버튼을 위한 공간 확보 */}
              <div className="h-32"></div>

              {/* 액션 버튼 */}
              <div className="fixed bottom-0 left-0 w-full h-24 bg-white border-t border-gray-200">
                <div className="max-w-[1600px] mx-auto px-4 h-full flex items-center justify-end gap-4">
                  <button
                    onClick={() => navigate('/parking/detail')}
                    className="w-44 h-16 bg-[#155acb] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                  >
                    <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                      신청 내역 확인
                    </div>
                  </button>
                  
                  {application.status === 'cancelled' && isApplicationPeriodActive() && (
                    <button
                      onClick={() => navigate('/parking/application', { state: { currentPeriod } })}
                      className="w-44 h-16 bg-green-600 rounded-sm hover:bg-green-700 transition-colors"
                    >
                      <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                        새 신청하기
                      </div>
                    </button>
                  )}
                  
                  {canModifyApplication() && (
                    <button
                      onClick={() => navigate('/parking/application', { state: { editMode: true, application, currentPeriod } })}
                      className="w-44 h-16 bg-green-600 rounded-sm hover:bg-green-700 transition-colors"
                    >
                      <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                        신청 내용 수정
                      </div>
                    </button>
                  )}

                  {canCancelApplication() && (
                    <button
                      onClick={() => navigate('/parking/detail')}
                      className="w-44 h-16 rounded-sm border border-[#ed5a5a] text-[#ed5a5a] hover:bg-red-50 transition-colors"
                    >
                      <div className="text-center text-[#ed5a5a] text-lg font-bold font-['Pretendard'] leading-9">
                        신청 취소
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* 신청 안내 카드 */}
              <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
                <div className="p-8 text-center">
                  <div className="mb-6">
                    <svg className="mx-auto h-16 w-16 text-[#6b7684]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                  <h2 className="text-[#1e2c49] text-2xl font-bold font-['Pretendard'] leading-9 mb-4">
                    웅동 임시 화물차 주차장 정기권 신청이 필요하신가요?
                  </h2>
                  <p className="text-[#6b7684] text-lg leading-relaxed mb-8">
                    {currentPeriod ? (
                      <>
                        개별화물차 정기주차를 신청하실 수 있습니다.<br />
                        {isApplicationPeriodActive() ? 
                          '현재 신청 접수 중입니다. 신청 후 관리자 검토를 거쳐 추첨이 진행됩니다.' :
                          getPeriodStatusText()
                        }
                      </>
                    ) : (
                      '현재 활성화된 신청 차수가 없습니다. 관리자에게 문의해 주세요.'
                    )}
                  </p>
                  {currentPeriod && isApplicationPeriodActive() ? (
                    <button
                      onClick={() => navigate('/parking/application', { state: { currentPeriod } })}
                      className="w-56 h-16 bg-[#155acb] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                    >
                      <div className="text-center text-white text-xl font-bold font-['Pretendard'] leading-9">
                        신청하기
                      </div>
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-56 h-16 bg-gray-400 rounded-sm cursor-not-allowed"
                    >
                      <div className="text-center text-white text-xl font-bold font-['Pretendard'] leading-9">
                        {currentPeriod ? '신청 기간이 아닙니다' : '신청 불가'}
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {/* 안내사항 */}
              <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
                <div className="p-8">
                  <h3 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    웅동 임시 화물차 주차장 정기권 신청 안내
                  </h3>
                  <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                  <div className="space-y-6 text-[#6b7684]">
                    <div>
                      <p className="text-[#1e2c49] text-lg font-semibold mb-3">신청 유형</p>
                      <ul className="ml-4 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-[#1b89e5] mt-1">•</span>
                          <span><strong>섀시업체 신청:</strong> 섀시업체 소속 차량을 위한 정기주차</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#1b89e5] mt-1">•</span>
                          <span><strong>개별화물차 신청:</strong> 개별 화물차를 위한 정기주차</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-[#1e2c49] text-lg font-semibold mb-3">신청 기간</p>
                      <p className="ml-4">1개월, 2개월, 3개월 중 선택 가능</p>
                    </div>
                    <div>
                      <p className="text-[#1e2c49] text-lg font-semibold mb-3">신청 절차</p>
                      <p className="ml-4">신청서 제출 → 관리자 검토 → 추첨 진행 → 결과 발표</p>
                    </div>
                    <div>
                      <p className="text-[#1e2c49] text-lg font-semibold mb-3">주의사항</p>
                      <p className="ml-4">신청 후 추첨이 완료되기 전까지는 수정 및 취소가 가능합니다.</p>
                    </div>
                                     </div>
                 </div>
               </div>

               {/* 하단 여백 - 스크롤을 위한 공간 확보 */}
               <div className="h-32"></div>
             </>
           )}
        </div>
      </div>
    </ClientLayout>
  );
};

export default ParkingMainPage; 